export const GENDER_OPTIONS = [
  { value: "MALE", label: "ذكر" },
  { value: "FEMALE", label: "أنثى" },
];

export const MARITAL_STATUS_OPTIONS = [
  { value: "SINGLE", label: "أعزب / عزباء" },
  { value: "ENGAGED", label: "خاطب / خاطبة" },
  { value: "MARRIED", label: "متزوج / متزوجة" },
  { value: "SEPARATED", label: "منفصل / منفصلة" },
];

export const MILITARY_STATUS_OPTIONS = [
  { value: "COMPLETED", label: "أدى الخدمة" },
  { value: "EXEMPTED", label: "معفى" },
  { value: "POSTPONED", label: "مؤجل" },
  { value: "NOT_CALLED_YET", label: "لم يُدعَ بعد" },
  { value: "NOT_APPLICABLE", label: "غير مطلوب" },
];

export const EDUCATION_LEVEL_OPTIONS = [
  "يقرأ ويكتب",
  "ابتدائية",
  "إعدادية",
  "ثانوية عامة / دبلوم",
  "فوق متوسط",
  "بكالوريوس / ليسانس",
  "ماجستير",
  "دكتوراه",
];

export const EMERGENCY_RELATION_OPTIONS = [
  "زوج / زوجة",
  "أب",
  "أم",
  "أخ / أخت",
  "ابن / ابنة",
];

export const YES_NO_OPTIONS = [
  { value: "yes", label: "نعم" },
  { value: "no", label: "لا" },
];

export function optionLabel(options, value) {
  if (!value) return "—";
  return options.find((item) => item.value === value)?.label || value;
}

export function toDateInputValue(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

export function completenessTone(percent = 0) {
  if (percent >= 100) return "complete";
  if (percent >= 70) return "good";
  if (percent >= 40) return "warn";
  return "low";
}

/**
 * Employee-submitted personal data only. Required documents (ID, contract, …)
 * are tracked separately so a finished profile does not look 79% complete.
 */
export function fieldCompleteness(completeness) {
  if (!completeness) {
    return {
      percent: 0,
      filled: 0,
      total: 0,
      isComplete: false,
      missingFields: [],
      missingDocuments: [],
    };
  }

  const fields = completeness.sections?.fields;
  if (Array.isArray(fields) && fields.length > 0) {
    const filled = fields.filter((item) => item.filled).length;
    const total = fields.length;
    return {
      percent: total ? Math.round((filled / total) * 100) : 0,
      filled,
      total,
      isComplete: filled === total,
      missingFields:
        completeness.missingFields ||
        fields.filter((item) => !item.filled).map((item) => item.label),
      missingDocuments: completeness.missingDocuments || [],
    };
  }

  if (typeof completeness.fieldsComplete === "boolean") {
    return {
      percent: completeness.fieldsComplete
        ? 100
        : (completeness.percent ?? 0),
      filled: completeness.fieldsComplete ? 1 : 0,
      total: 1,
      isComplete: completeness.fieldsComplete,
      missingFields: completeness.missingFields || [],
      missingDocuments: completeness.missingDocuments || [],
    };
  }

  return {
    percent: completeness.percent ?? 0,
    filled: completeness.filled ?? 0,
    total: completeness.total ?? 0,
    isComplete: Boolean(completeness.isComplete),
    missingFields: completeness.missingFields || [],
    missingDocuments: completeness.missingDocuments || [],
  };
}

export function formatProfileMissingSummary(completeness) {
  const view = fieldCompleteness(completeness);
  return [
    ...view.missingFields,
    ...view.missingDocuments.map((label) => `مستند: ${label}`),
  ];
}

const ALWAYS_REQUIRED_FORM_FIELDS = [
  ["legalFullName", "الاسم بالكامل كما في الرقم القومي"],
  ["phone", "رقم الهاتف"],
  ["nationalId", "الرقم القومي"],
  ["gender", "النوع"],
  ["birthDate", "تاريخ الميلاد"],
  ["address", "العنوان"],
  ["maritalStatus", "الحالة الاجتماعية"],
  ["emergencyContactName", "اسم قريب الدرجة الأولى"],
  ["emergencyContactPhone", "هاتف قريب الدرجة الأولى (للطوارئ)"],
  ["emergencyContactRelation", "صلة القرابة"],
  ["fatherJob", "وظيفة الأب"],
  ["motherJob", "وظيفة الأم"],
  ["siblingsCount", "عدد الإخوة / الأخوات"],
  ["courses", "الكورسات والتدريبات"],
  ["hasChronicDisease", "أمراض مزمنة"],
  ["hasCompanyRelatives", "أقارب داخل الشركة"],
  ["careerGoal", "الوظيفة التي يطمح لها"],
  ["educationLevel", "المؤهل الدراسي"],
  ["educationInstitution", "جهة التعليم"],
  ["graduationYear", "سنة التخرج"],
];

function isFormValueFilled(value) {
  if (value === true || value === false) return true;
  if (typeof value === "number") return !Number.isNaN(value);
  return String(value ?? "").trim().length > 0;
}

/** Live required-field status for the employee self-edit form. Bank and insurance fields are not collected. */
export function requiredProfileFieldStatus(form = {}) {
  const items = ALWAYS_REQUIRED_FORM_FIELDS.map(([key, label]) => ({
    key,
    label,
    filled: isFormValueFilled(form[key]),
  }));

  if (form.gender === "MALE") {
    items.push({
      key: "militaryStatus",
      label: "الموقف من التجنيد",
      filled: isFormValueFilled(form.militaryStatus),
    });
  }
  if (form.maritalStatus === "MARRIED") {
    items.push({
      key: "childrenCount",
      label: "عدد الأبناء",
      filled: isFormValueFilled(form.childrenCount),
    });
  }
  if (form.hasChronicDisease === "yes") {
    items.push(
      {
        key: "chronicDiseaseDetails",
        label: "تفاصيل المرض المزمن",
        filled: isFormValueFilled(form.chronicDiseaseDetails),
      },
      {
        key: "chronicDiseaseMedication",
        label: "العلاج الحالي",
        filled: isFormValueFilled(form.chronicDiseaseMedication),
      },
    );
  }
  if (form.hasCompanyRelatives === "yes") {
    items.push({
      key: "companyRelativeDetails",
      label: "درجة القرابة داخل الشركة",
      filled: isFormValueFilled(form.companyRelativeDetails),
    });
  }

  const filled = items.filter((item) => item.filled).length;
  const total = items.length;
  return {
    filled,
    total,
    percent: total ? Math.round((filled / total) * 100) : 0,
    isComplete: filled === total,
    missing: items.filter((item) => !item.filled).map((item) => item.label),
  };
}
